import React, { useState } from 'react';
import { Button } from '../ui/Button';
import { CheckIcon, ShieldCheckIcon, ClockIcon, CreditCardIcon, HeartIcon, BadgeCheckIcon, ArrowRightIcon, PhoneIcon } from 'lucide-react';
import { Toggle } from '../ui/Toggle';
import { useNavigate } from 'react-router-dom';
export const Pricing = () => {
  const navigate = useNavigate();
  const [activeToggleIndex, setActiveToggleIndex] = useState(0);
  const openCalendly = () => {
    window.open('https://calendly.com/zerototendigital/exploration-feature-design', '_blank');
  };
  const seniorPMPlans = [{
    name: 'Starter',
    price: '$3,100',
    period: 'per month',
    hourlyRate: '$78/hour',
    hours: '8-10 hours/week',
    description: 'For early-stage teams',
    leadPmNote: 'Includes a dedicated Senior PM (your day-to-day partner)',
    aiNote: 'Includes AI-powered market analysis and prototype mockups',
    features: ['Product roadmapping', 'Sprint planning', 'Basic user research', 'Weekly check-ins', 'Email support'],
    popular: false,
    buttonText: 'Speak with a Senior PM',
    buttonIcon: <ArrowRightIcon className="ml-2 h-4 w-4" data-id="element-372" />,
    buttonVariant: 'secondary',
    onClick: openCalendly
  }, {
    name: 'Growth',
    price: '$5,999',
    period: 'per month',
    hourlyRate: '$69/hour',
    hours: '15-20 hours/week',
    description: 'For growing companies',
    leadPmNote: 'Includes a dedicated Senior PM (your day-to-day partner)',
    aiNote: 'In-depth AI/ML feature exploration, prototype development and testing',
    features: ['Full product strategy', 'Comprehensive roadmapping', 'In-depth user research', 'PRD development', '2x weekly check-ins', 'Priority support'],
    popular: true,
    fastAssignment: true,
    buttonText: 'Speak with a Senior PM',
    buttonIcon: <ArrowRightIcon className="ml-2 h-4 w-4" data-id="element-373" />,
    buttonVariant: 'primary'
  }, {
    name: 'Scale',
    price: '$11,999',
    period: 'per month',
    hourlyRate: '$73/hour',
    hours: '35-40 hours/week',
    description: 'For full lifecycle ownership',
    leadPmNote: 'Includes a dedicated Lead PM (your day-to-day partner)',
    aiNote: 'Full lifecycle AI product strategy, including advanced prototypes and user validation',
    features: ['Dedicated senior PM', 'Full product lifecycle management', 'Market & competitive analysis', 'Executive reporting', 'Team management', 'Daily standups', '24/7 priority support'],
    popular: false,
    buttonText: 'Speak with a Senior PM',
    buttonIcon: <ArrowRightIcon className="ml-2 h-4 w-4" data-id="element-374" />,
    buttonVariant: 'secondary'
  }, {
    name: 'Custom',
    price: 'Custom',
    period: 'pricing',
    hourlyRate: 'Tailored',
    hours: 'Flexible hours',
    description: 'For enterprise organizations',
    leadPmNote: 'Includes a dedicated Lead PM (your day-to-day partner)',
    aiNote: 'Tailored AI solutions and custom prototypes—integrated with your tech stack',
    features: ['Fully customized solutions', 'Dedicated PM team', 'Executive strategy sessions', 'Advanced AI integrations', 'Custom reporting dashboards', 'Strategic consulting', 'Priority access to new features', 'Dedicated account manager'],
    popular: false,
    buttonText: 'Speak with a Senior PM',
    buttonIcon: <PhoneIcon className="ml-2 h-4 w-4" data-id="element-375" />,
    buttonVariant: 'outline'
  }];
  const leadershipPlans = [{
    name: 'Director',
    price: '$18,999',
    period: 'per month',
    description: 'For growing organizations',
    leadPmNote: 'Includes a dedicated Lead PM (your day-to-day partner)',
    aiNote: 'Advanced AI strategy and prototype development for key initiatives',
    features: ['Full leadership oversight', 'Strategic product direction', 'Cross-functional coordination', 'Product portfolio management', 'Executive stakeholder management', 'Team coaching & mentoring', 'Weekly executive briefings', 'Priority 24/7 support'],
    popular: false,
    buttonText: 'Speak with a Senior PM'
  }, {
    name: 'VP',
    price: '$24,999',
    period: 'per month',
    description: 'For scaling companies',
    leadPmNote: 'Includes a dedicated Lead PM (your day-to-day partner)',
    aiNote: 'Enterprise AI strategy with comprehensive prototype program',
    features: ['Full product org leadership', 'Product vision development', 'Strategic roadmap alignment', 'Executive team collaboration', 'Investor relations support', 'Team hiring & structure planning', 'Bi-weekly board updates', 'Dedicated executive support'],
    popular: true,
    fastAssignment: true,
    buttonText: 'Speak with a Senior PM'
  }, {
    name: 'CPO',
    price: '$29,999',
    period: 'per month',
    description: 'For enterprise businesses',
    leadPmNote: 'Includes a dedicated Lead PM (your day-to-day partner)',
    aiNote: 'Full AI transformation strategy with executive-level prototyping program',
    features: ['C-suite product leadership', 'Company-wide product strategy', 'Product-led growth expertise', 'M&A product integration', 'Board & investor presentations', 'Executive team alignment', 'On-demand strategic consulting', 'White-glove support'],
    popular: false,
    buttonText: 'Speak with a Senior PM'
  }, {
    name: 'Custom',
    price: 'Contact',
    period: 'for details',
    description: 'For unique requirements',
    leadPmNote: 'Includes a dedicated Lead PM (your day-to-day partner)',
    aiNote: 'Custom AI and prototyping solutions for your specific needs',
    features: ['Tailored leadership solutions', 'Custom engagement models', 'Specialized industry expertise', 'Multi-product portfolio strategy', 'International market expertise', 'Enterprise transformation', 'Specialized team structures', 'Custom reporting & metrics'],
    popular: false,
    buttonText: 'Speak with a Senior PM'
  }];
  const plans = activeToggleIndex === 0 ? seniorPMPlans : leadershipPlans;
  return <section id="pricing" className="py-20 bg-white relative overflow-hidden" data-id="element-376">
      {/* Background pattern elements */}
      <div className="absolute top-0 right-0 w-72 h-72 bg-purple-50 rounded-full opacity-50 -mr-36 -mt-36" data-id="element-377"></div>
      <div className="absolute bottom-0 left-0 w-80 h-80 bg-purple-50 rounded-full opacity-40 -ml-40 -mb-40" data-id="element-378"></div>
      <div className="container mx-auto px-4 md:px-6 relative z-10" data-id="element-379">
        <div className="text-center mb-12" data-id="element-380">
          <h2 className="text-4xl md:text-5xl font-bold mb-4 text-black" data-id="element-381">
            Simple, Transparent Pricing
          </h2>
          <p className="text-xl text-text-secondary max-w-2xl mx-auto" data-id="element-382">
            Choose the plan that works for your business needs.
          </p>
        </div>
        {/* Guarantee Badge */}
        <div className="flex justify-center mb-8" data-id="element-383">
          <div className="bg-purple-50 rounded-full px-5 py-2 inline-flex items-center" data-id="element-384">
            <ShieldCheckIcon className="h-5 w-5 text-primary mr-2" data-id="element-385" />
            <span className="text-sm font-medium" data-id="element-386">
              Lead PM in 48h • AI & Prototype support included • Interview
              optional
            </span>
          </div>
        </div>
        <div className="flex justify-center mb-10" data-id="element-387">
          <Toggle options={['Senior PM', 'Leadership']} activeIndex={activeToggleIndex} onChange={setActiveToggleIndex} data-id="element-388" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto" data-id="element-389">
          {plans.map((plan, index) => <div key={index} className={`bg-white rounded-xl border ${plan.popular ? 'border-primary shadow-lg ring-2 ring-primary ring-opacity-20' : 'border-gray-200 shadow-sm hover:shadow-md'} transition-all duration-300 flex flex-col h-full relative`} data-id="element-390">
              {plan.popular && <div className="absolute -top-4 -right-4" data-id="element-391">
                  <div className="bg-primary text-white text-xs font-bold px-3 py-1 rounded-full shadow-md flex items-center" data-id="element-392">
                    <BadgeCheckIcon className="h-3 w-3 mr-1" data-id="element-393" />
                    MOST POPULAR
                  </div>
                </div>}
              {plan.fastAssignment && <div className="absolute -top-4 left-4" data-id="element-394"></div>}
              <div className="p-6 border-b border-gray-100" data-id="element-395">
                <h3 className="text-xl font-bold text-black mb-1" data-id="element-396">
                  {plan.name}
                </h3>
                <p className="text-sm text-gray-500 mb-2" data-id="element-397">{plan.description}</p>
                <p className="text-xs text-primary mb-2" data-id="element-398">{plan.leadPmNote}</p>
                <p className="text-xs text-green-600 mb-4" data-id="element-399">{plan.aiNote}</p>
                <div className="flex items-baseline mb-1" data-id="element-400">
                  <span className="text-3xl font-bold text-black" data-id="element-401">
                    {plan.price}
                  </span>
                  <span className="text-gray-500 ml-1 text-sm" data-id="element-402">
                    {plan.period}
                  </span>
                </div>
                <div className="flex flex-col space-y-2 mb-4" data-id="element-403">
                  <div className="flex items-center text-primary-dark font-medium" data-id="element-404">
                    <ClockIcon className="w-4 h-4 mr-2" data-id="element-405" />
                    <span className="text-lg" data-id="element-406">{plan.hours}</span>
                  </div>
                  <div className="text-sm text-gray-500" data-id="element-407">
                    Effective rate: {plan.hourlyRate}
                  </div>
                </div>
              </div>
              <div className="p-6 flex-grow" data-id="element-408">
                <ul className="space-y-3 mb-6" data-id="element-409">
                  {plan.features.map((feature, idx) => <li key={idx} className="flex items-start" data-id="element-410">
                      <div className="flex-shrink-0 mt-1" data-id="element-411">
                        <div className="w-4 h-4 rounded-full bg-primary bg-opacity-10 flex items-center justify-center" data-id="element-412">
                          <CheckIcon className="w-3 h-3 text-primary" data-id="element-413" />
                        </div>
                      </div>
                      <span className="text-gray-700 ml-3 text-sm" data-id="element-414">
                        {feature}
                      </span>
                    </li>)}
                </ul>
              </div>
              <div className="p-6 pt-0 mt-auto" data-id="element-415">
                <Button variant={plan.buttonVariant as any} className={`w-full ${plan.popular ? 'bg-primary text-white hover:bg-primary-dark' : ''}`} onClick={openCalendly} data-id="element-416">
                  {plan.buttonText}
                  {plan.buttonIcon}
                </Button>
              </div>
            </div>)}
        </div>
        {/* Our Guarantees Section */}
        <div className="mt-24 max-w-5xl mx-auto" data-id="element-417">
          <div className="text-center mb-12" data-id="element-418">
            <h3 className="text-2xl md:text-3xl font-bold text-black" data-id="element-419">
              Our Guarantees
            </h3>
            <p className="text-text-secondary mt-3" data-id="element-420">
              We stand behind our service with these promises
            </p>
            <div className="inline-block bg-green-50 text-green-600 px-4 py-2 rounded-full text-sm font-medium mt-4" data-id="element-421">
              AI & prototyping expertise guaranteed – if your PM isn't a fit,
              we'll swap or refund 75% within 14 days.
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-422">
            <div className="bg-purple-50 rounded-xl p-6 flex items-start" data-id="element-423">
              <div className="bg-white p-3 rounded-full mr-4 shadow-sm" data-id="element-424">
                <ShieldCheckIcon className="w-6 h-6 text-primary" data-id="element-425" />
              </div>
              <div data-id="element-426">
                <h4 className="font-bold text-lg mb-2" data-id="element-427">
                  Satisfaction Guarantee
                </h4>
                <p className="text-text-secondary text-sm" data-id="element-428">
                  If you're not satisfied within the first 14 days, we'll refund
                  75% of your subscription – no questions asked.{' '}
                  <a href="/refund-policy" className="text-primary hover:underline" data-id="element-429">
                    View our full policy
                  </a>
                  .
                </p>
              </div>
            </div>
            <div className="bg-purple-50 rounded-xl p-6 flex items-start" data-id="element-430">
              <div className="bg-white p-3 rounded-full mr-4 shadow-sm" data-id="element-431">
                <ClockIcon className="w-6 h-6 text-primary" data-id="element-432" />
              </div>
              <div data-id="element-433">
                <h4 className="font-bold text-lg mb-2" data-id="element-434">Quick Onboarding</h4>
                <p className="text-text-secondary text-sm" data-id="element-435">
                  Get matched with your PM and start working within 48 hours. No
                  lengthy hiring processes.
                </p>
              </div>
            </div>
            <div className="bg-purple-50 rounded-xl p-6 flex items-start" data-id="element-436">
              <div className="bg-white p-3 rounded-full mr-4 shadow-sm" data-id="element-437">
                <CreditCardIcon className="w-6 h-6 text-primary" data-id="element-438" />
              </div>
              <div data-id="element-439">
                <h4 className="font-bold text-lg mb-2" data-id="element-440">Flexible Billing</h4>
                <p className="text-text-secondary text-sm" data-id="element-441">
                  Change or cancel your plan anytime. Scale up during busy
                  periods and scale down when needed.
                </p>
              </div>
            </div>
            <div className="bg-purple-50 rounded-xl p-6 flex items-start" data-id="element-442">
              <div className="bg-white p-3 rounded-full mr-4 shadow-sm" data-id="element-443">
                <HeartIcon className="w-6 h-6 text-primary" data-id="element-444" />
              </div>
              <div data-id="element-445">
                <h4 className="font-bold text-lg mb-2" data-id="element-446">Quality Commitment</h4>
                <p className="text-text-secondary text-sm" data-id="element-447">
                  All our PMs have 5+ years of experience with AI and
                  prototyping at top tech companies. We only work with the best.
                </p>
              </div>
            </div>
          </div>
        </div>
        {/* Get Started Section */}
        <div className="mt-24 mb-8 max-w-4xl mx-auto bg-gradient-to-r from-primary to-primary-light rounded-2xl overflow-hidden shadow-lg" data-id="element-448">
          <div className="px-8 py-12 text-center" data-id="element-449">
            <h3 className="text-2xl md:text-3xl font-bold text-white mb-4" data-id="element-450">
              Ready to supercharge your product with AI?
            </h3>
            <p className="text-white text-opacity-90 mb-8 max-w-2xl mx-auto" data-id="element-451">
              Join our roster of clients who trust us with their product
              management needs.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-452">
              <Button variant="secondary" className="bg-white text-primary hover:bg-gray-100 border-0" onClick={openCalendly} data-id="element-453">
                Speak with a Senior PM
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
};